import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Overview from "@/pages/Overview";
import EmailInbox from "@/pages/EmailInbox";
import Pipeline from "@/pages/Pipeline";
import Leads from "@/pages/Leads";
import LeadProfile from "@/pages/LeadProfile";
import Campaigns from "@/pages/Campaigns";
import Automations from "@/pages/Automations";
import Rewards from "@/pages/Rewards";
import FacebookOAuthCallback from "@/pages/FacebookOAuthCallback";
import InstagramOAuthCallback from "@/pages/InstagramOAuthCallback";
import GbpOAuthCallback from "@/pages/GbpOAuthCallback";
import Integrations from "@/pages/Integrations";
import SuperAdminTremendous from "@/pages/SuperAdminTremendous";
import MarketingDashboard from "@/pages/MarketingDashboard";
import Settings from "@/pages/Settings";
import Services from "@/pages/Services";
import Customers from "@/pages/Customers";
import CustomerProfile from "@/pages/CustomerProfile";
import SmartEstimator from "@/pages/SmartEstimator";
import Estimates from "@/pages/Estimates";
import EstimateEditor from "@/pages/EstimateEditor";
import EstimatePublic from "@/pages/EstimatePublic";
import Jobs from "@/pages/Jobs";
import JobProfile from "@/pages/JobProfile";
import Invoices from "@/pages/Invoices";
import InvoiceProfile from "@/pages/InvoiceProfile";
import InvoicePublic from "@/pages/InvoicePublic";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import SuperAdmin from "@/pages/SuperAdmin";
import SuperAdminFeedback from "@/pages/SuperAdminFeedback";
import Onboarding from "@/pages/Onboarding";
import Imports from "@/pages/Imports";
import ImportWizard from "@/pages/ImportWizard";
import HistoricalImports from "@/pages/HistoricalImports";
import HistoricalImportBatch from "@/pages/HistoricalImportBatch";
import Financials from "@/pages/Financials";
import Expenses from "@/pages/Expenses";
import Mileage from "@/pages/Mileage";
import Statements from "@/pages/Statements";
import DocumentDesigner from "@/pages/DocumentDesigner";
import Coach from "@/pages/Coach";
import CoachApprovalLog from "@/pages/CoachApprovalLog";
import CoachCorrectionsReport from "@/pages/CoachCorrectionsReport";
import CoachRulesLab from "@/pages/CoachRulesLab";
import CoachVerificationCenter from "@/pages/CoachVerificationCenter";
import CoachImportPreflight from "@/pages/CoachImportPreflight";
import CoachAdminPanel from "@/pages/CoachAdminPanel";
import EmailBrandingSettings from "@/pages/EmailBrandingSettings";
import Payments from "@/pages/Payments";
import Reviews from "@/pages/Reviews";
import RequestAccess from "@/pages/RequestAccess";
import AdminAccessRequests from "@/pages/AdminAccessRequests";
import AdminUnmatchedEmails from "@/pages/AdminUnmatchedEmails";
import OAuthConsent, { peekReturnTo } from "@/pages/OAuthConsent";
import ReceiptUpload from "@/pages/ReceiptUpload";

function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={peekReturnTo() || "/app"} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing/>} />
            <Route path="/login" element={<AuthRedirect><Login/></AuthRedirect>} />
            <Route path="/register" element={<Navigate to="/request-access" replace/>} />
            <Route path="/signup" element={<Navigate to="/request-access" replace/>} />
            <Route path="/request-access" element={<RequestAccess/>} />
            <Route path="/forgot-password" element={<ForgotPassword/>} />
            <Route path="/reset-password/:token" element={<ResetPassword/>} />
            <Route path="/accept-invite/:token" element={<AcceptInvite/>} />
            {/* Public estimate view */}
            <Route path="/estimate/:token" element={<EstimatePublic/>} />
            <Route path="/invoice/:token" element={<InvoicePublic/>} />
            <Route path="/oauth/consent" element={<OAuthConsent/>} />
            <Route path="/receipt-upload/:token" element={<ReceiptUpload/>} />
            <Route path="/onboarding" element={<ProtectedRoute skipOnboarding><Onboarding/></ProtectedRoute>} />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview/>} />
              <Route path="pipeline" element={<Pipeline/>} />
              <Route path="leads" element={<Leads/>} />
              <Route path="leads/:id" element={<LeadProfile/>} />
              <Route path="customers" element={<Customers/>} />
              <Route path="customers/:id" element={<CustomerProfile/>} />
              <Route path="customers/:id/estimator" element={<SmartEstimator/>} />
              <Route path="estimates" element={<Estimates/>} />
              <Route path="estimates/new" element={<EstimateEditor/>} />
              <Route path="estimates/:id" element={<EstimateEditor/>} />
              <Route path="jobs" element={<Jobs/>} />
              <Route path="jobs/:id" element={<JobProfile/>} />
              <Route path="invoices" element={<Invoices/>} />
              <Route path="invoices/:id" element={<InvoiceProfile/>} />
              <Route path="payments" element={<Payments/>} />
              <Route path="reviews" element={<Reviews/>} />
              <Route path="access-requests" element={<AdminAccessRequests/>} />
              <Route path="admin/unmatched-emails" element={<AdminUnmatchedEmails/>} />
              <Route path="email-inbox" element={<EmailInbox/>} />
              <Route path="services" element={<Services/>} />
              <Route path="imports" element={<Imports/>} />
              <Route path="imports/historical" element={<HistoricalImports/>} />
              <Route path="imports/historical/:id" element={<HistoricalImportBatch/>} />
              <Route path="imports/:id" element={<ImportWizard/>} />
              <Route path="financials" element={<Financials/>} />
              <Route path="financials/expenses" element={<Expenses/>} />
              <Route path="expenses" element={<Expenses/>} />
              <Route path="financials/mileage" element={<Mileage/>} />
              <Route path="financials/statements" element={<Statements/>} />
              <Route path="campaigns" element={<Campaigns/>} />
              <Route path="automations" element={<Automations/>} />
              <Route path="rewards" element={<Rewards/>} />
              <Route path="settings" element={<Settings/>} />
              <Route path="integrations" element={<Integrations/>} />
              <Route path="marketing-dashboard" element={<MarketingDashboard/>} />
              <Route path="document-designer" element={<DocumentDesigner/>} />
              <Route path="coach" element={<Coach/>} />
              <Route path="coach/approval-log" element={<CoachApprovalLog/>} />
              <Route path="coach/corrections" element={<CoachCorrectionsReport/>} />
              <Route path="coach/rules" element={<CoachRulesLab/>} />
              <Route path="coach/verification" element={<CoachVerificationCenter/>} />
              <Route path="coach/import-preflight" element={<CoachImportPreflight/>} />
              <Route path="admin/coach-llm" element={<CoachAdminPanel/>} />
              <Route path="settings/email-branding" element={<EmailBrandingSettings/>} />
              <Route path="superadmin" element={<SuperAdmin/>} />
              <Route path="superadmin/tremendous" element={<SuperAdminTremendous/>} />
              <Route path="superadmin/feedback" element={<SuperAdminFeedback/>} />
            </Route>
            <Route path="/facebook-oauth-callback" element={<FacebookOAuthCallback/>} />
            <Route path="/instagram-oauth-callback" element={<InstagramOAuthCallback/>} />
            <Route path="/gbp-oauth-callback" element={<GbpOAuthCallback/>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
